import { useEffect, useState } from "react";
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Banknote, Ticket, BusFront, Route, Users, Wallet, Loader2, Globe2, Coins } from "lucide-react";
import { StatCard } from "@/components/Ui";
import { api } from "@/lib/api";
import { money, fmtDate, fmtTime } from "@/lib/format";

export default function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0052FF]" /></div>;
  return (
    <div className="space-y-5" data-testid="admin-dashboard">
      <h2 className="font-display text-2xl font-extrabold tracking-tight">Today at a glance</h2>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard label="Revenue today" value={money(s.revenue_today)} icon={Banknote} testid="stat-revenue-today" />
        <StatCard label="Tickets today" value={s.tickets_today} icon={Ticket} tint="bg-sky-50 text-sky-600" testid="stat-tickets-today" />
        <StatCard label="Trips today" value={s.trips_today} sub={`${s.departed_today} released`} icon={Route} tint="bg-emerald-50 text-emerald-600" testid="stat-trips-today" />
        <StatCard label="Active fleet" value={s.active_buses} sub={`${s.active_drivers} drivers`} icon={BusFront} tint="bg-amber-50 text-amber-600" testid="stat-active-buses" />
        <StatCard label="Open teller shifts" value={s.open_shifts} icon={Users} testid="stat-open-shifts" />
        <StatCard label="Awaiting cash handover" value={s.pending_handovers} icon={Wallet} tint="bg-rose-50 text-rose-600" testid="stat-pending-handovers" />
        <StatCard label="Online vs counter" value={`${money(s.by_channel.online)}`} sub={`Counter ${money(s.by_channel.counter)}`} icon={Globe2} tint="bg-indigo-50 text-indigo-600" testid="stat-channel" />
        <StatCard label="Buses due sign-out" value={s.signout_due} icon={BusFront} tint="bg-rose-50 text-rose-600" testid="stat-signout-due" />
        <StatCard label="Awaiting disbursement" value={s.pending_disbursements} icon={Wallet} tint="bg-amber-50 text-amber-600" testid="stat-pending-disbursements" />
        <StatCard label="MoMo / Card / Cash" value={money(s.by_method.momo)} sub={`Card ${money(s.by_method.card)} · Cash ${money(s.by_method.cash)}`} icon={Coins} tint="bg-sky-50 text-sky-600" testid="stat-methods" />
      </div>
      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <div className="card-soft p-5">
          <div className="label-xs mb-3">Revenue - last 7 days</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={s.daily.map((d) => ({ ...d, day: fmtDate(d.date, { weekday: "short", day: "numeric" }) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} width={50} />
                <Tooltip formatter={(v) => money(v)} cursor={{ fill: "#EFF6FF" }} />
                <Bar dataKey="revenue" fill="#0052FF" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card-soft p-5">
          <div className="label-xs mb-3">Top routes (7 days)</div>
          {s.top_routes.length ? s.top_routes.map((r) => (
            <div key={r.route} className="flex justify-between border-b border-slate-100 py-2 text-sm"><span className="font-semibold">{r.route}</span><span>{money(r.revenue)}</span></div>
          )) : <p className="text-sm text-slate-500">No sales yet.</p>}
        </div>
      </div>
      <div className="card-soft p-5">
        <div className="label-xs mb-3">Next departures today</div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {s.upcoming.map((t) => (
            <div key={t.id} className="rounded-xl border border-slate-200 p-3">
              <div className="font-display text-lg font-extrabold">{fmtTime(t.departure)} <span className="text-sm font-semibold text-slate-500">{t.origin_city} → {t.destination_city}</span></div>
              <div className="text-xs text-slate-500">{t.bus_number} · {t.booked}/{t.capacity} booked</div>
            </div>
          ))}
          {!s.upcoming.length && <p className="text-sm text-slate-500">No more departures today.</p>}
        </div>
      </div>
    </div>
  );
}
